import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button, Field, Input, Spinner,
  Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow, Title3,
} from "@fluentui/react-components";
import { api } from "../../api/client";

interface QueueMember { user: { id: string; displayName: string; email: string } }
interface AdminQueue {
  id: string; name: string; slug: string; description?: string | null;
  isActive: boolean; members: QueueMember[];
}

export function AdminQueuesPage() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["queues", "admin"], queryFn: () => api.get<AdminQueue[]>("/api/queues") });
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [addUserId, setAddUserId] = useState<Record<string, string>>({});

  const create = useMutation({
    mutationFn: () => api.post("/api/queues", { name, slug }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["queues"] }); setName(""); setSlug(""); },
  });
  const addMember = useMutation({
    mutationFn: (input: { queueId: string; userId: string }) =>
      api.post(`/api/queues/${input.queueId}/members`, { userId: input.userId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["queues"] }),
  });
  const removeMember = useMutation({
    mutationFn: (input: { queueId: string; userId: string }) =>
      api.del(`/api/queues/${input.queueId}/members/${input.userId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["queues"] }),
  });

  return (
    <div>
      <Title3>Queues</Title3>
      <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginTop: 16 }}>
        <Field label="Name"><Input value={name} onChange={(_, d) => setName(d.value)} /></Field>
        <Field label="Slug"><Input value={slug} onChange={(_, d) => setSlug(d.value)} /></Field>
        <Button appearance="primary" disabled={!name || !slug} onClick={() => create.mutate()}>Add queue</Button>
      </div>
      <div style={{ marginTop: 16 }}>
        {list.isLoading ? <Spinner /> : (list.data ?? []).map((q) => (
          <div key={q.id} style={{ marginBottom: 24, padding: 12, border: "1px solid #eee", borderRadius: 4 }}>
            <h4 style={{ margin: 0 }}>{q.name} <small>({q.slug})</small></h4>
            <Table size="extra-small" style={{ marginTop: 8 }}>
              <TableHeader>
                <TableRow><TableHeaderCell>Member</TableHeaderCell><TableHeaderCell>Email</TableHeaderCell><TableHeaderCell /></TableRow>
              </TableHeader>
              <TableBody>
                {q.members.map((m) => (
                  <TableRow key={m.user.id}>
                    <TableCell>{m.user.displayName}</TableCell>
                    <TableCell>{m.user.email}</TableCell>
                    <TableCell>
                      <Button size="small"
                        onClick={() => removeMember.mutate({ queueId: q.id, userId: m.user.id })}>Remove</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Input
                placeholder="User ID (from Users page)"
                value={addUserId[q.id] ?? ""}
                onChange={(_, d) => setAddUserId({ ...addUserId, [q.id]: d.value })}
              />
              <Button onClick={() => {
                const uid = addUserId[q.id];
                if (uid) addMember.mutate({ queueId: q.id, userId: uid });
              }}>Add member</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
